// --------------------------------------------------------
// CARE LINE LIST - CareLineList
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import React, { useCallback, useEffect, useState } from "react";
import ContainerWithSidebar from "../../components/UI/containerWithSidebar/ContainerWithSidebar";
import {
  GeneralContainer,
  Text,
} from "../../components/UI/generalLayout/Layout.styled";
import Colors from "../../constants/Colors";
import ScreenHeader from "../../components/UI/screenHeader/ScreenHeader";
import LabelInput from "../../components/labelInput/LabelInput";
import SearchIcon from "@mui/icons-material/Search";
import ItemList from "../../components/itemList/ItemList";
import { CompanyListItemContainer } from "../company/CompanyList.styled";
import CareLineListItem from "../../components/careLineListItem/CareLineListItem";
import { useSelector } from "react-redux";
import CareLine from "../../models/core/careLine/CareLine";
import { useAppDispatch } from "../..";
import * as careLineActions from "../../store/actions/careLine";
import { useNavigate } from "react-router-dom";
import { Paths } from "../../enums/Paths";
import { executeWithDelay } from "../../helpers/HelperFunctions";

// --------------------------------------------------------
// CARE LINE LIST INTERFACE
// --------------------------------------------------------

/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */
function CareLineList(): JSX.Element {
  // --------------------------------------------------------
  // CARE LINE LIST PRIVATE DECLARATIONS
  // --------------------------------------------------------
  const [searchCareLineQuery, setSearchCareLineQuery] = useState("");
  const [careLineHeaderFields] = useState([
    { name: "Nome da linha de cuidado", width: "80%", align: null },
    { name: "Ativar/Desativar", width: "20%", align: "right" },
  ]);
  const careLineList: CareLine[] = useSelector(
    (state: any) => state.careLine.careLines
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getData = useCallback(async () => {
    await dispatch(careLineActions.fetchCareLines());
  }, [dispatch]);

  const filterDataByName = useCallback(async () => {
    if (searchCareLineQuery.length >= 3 || searchCareLineQuery === "") {
      executeWithDelay(500, async () => {
        await dispatch(
          careLineActions.filterCareLineByName(searchCareLineQuery)
        );
      });
    }
  }, [searchCareLineQuery]);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    filterDataByName();
  }, [searchCareLineQuery]);

  const navigateToRegisterCareLine = () => {
    return navigate(Paths.RegisterCareLine);
  };

  const navigateToRegisterCareLineEdit = (careLine: any) => {
    return navigate(Paths.RegisterCareLine, { state: { careLine: careLine } });
  };

  const navigateToRegisterCareLineView = (careLine: any) => {
    return navigate(Paths.RegisterCareLine, {
      state: { careLine: careLine, isView: true },
    });
  };

  const activateOrInactivateCareLine = async (
    id: string,
    isActive: boolean
  ) => {
    await dispatch(careLineActions.activateOrDeactivateCareLine(isActive, id));
    getData();
  };

  return (
    <ContainerWithSidebar>
      <GeneralContainer>
        <ScreenHeader
          screenTitle={"Linhas de Cuidado"}
          rightButtonText={"Cadastrar nova linha de cuidado"}
          onRightButtonClick={navigateToRegisterCareLine}
        />
        <LabelInput
          value={searchCareLineQuery}
          placeholder="Busca de linha de cuidado"
          onChangeText={(event: any) => {
            setSearchCareLineQuery(event.target.value);
          }}
          rightIcon={<SearchIcon />}
        />
        {searchCareLineQuery.length > 2 && (
          <div style={{ paddingLeft: 12, paddingRight: 12 }}>
            <Text color={Colors.lightBlue}>
              Resultados para: {searchCareLineQuery}{" "}
            </Text>
          </div>
        )}
        <ItemList listHeader={careLineHeaderFields} list={careLineList}>
          <CompanyListItemContainer>
            {careLineList.map((careLine) => {
              return (
                <CareLineListItem
                  id={careLine.id}
                  onActivateInactivate={activateOrInactivateCareLine}
                  onView={() => {
                    navigateToRegisterCareLineView(careLine);
                  }}
                  onEdit={() => {
                    navigateToRegisterCareLineEdit(careLine);
                  }}
                  name={careLine.nome}
                  isActiveRegister={!careLine.inativo}
                />
              );
            })}
          </CompanyListItemContainer>
        </ItemList>
      </GeneralContainer>
    </ContainerWithSidebar>
  );
}

export default CareLineList;
